import { formatDate } from 'date-fns'
import { ReadonlyURLSearchParams } from 'next/navigation'
import { setSearchParams, toSearchParams } from 'utils/searchParams'
import { headersCampus } from './headers'
import { Branche, Domaine, Lieux, Module, Organisme, Page, Paging, Refs, SearchContext, Session, Stage, Thematique } from './model'
import { OidcSession } from './oidc-config'
import { MAINTENANCE_MODE } from './server'

async function fetchCampusAPI<T>(path: string, options?: { params?: URLSearchParams, paging?: Paging, authSession?: OidcSession | null }): Promise<T> {
    let params = options?.params
    if (options?.paging) {
        params = setSearchParams(params ?? new URLSearchParams(), {
            'page': options.paging.page?.toFixed(0),
            'par-page': options.paging.parPage?.toFixed(0),
        })
    }
    const url = `${path}${params ? `?${params.toString()}` : ''}`

    let res = await fetch(
        `${process.env.NEXT_PUBLIC_CAMPUS_BASE_API_URL}${url}`,
        { headers: headersCampus() },
    )

    if (res.status === 503) {
        throw new Error(MAINTENANCE_MODE)
    }

    if (res.status === 401) {
        // 401 => session expirée, on relance sans authentification
        // Le logout sera géré par le composant Auth
        res = await fetch(url, { headers: headersCampus() })
    }

    if (!res.ok)
        throw new Error('Error ' + res.status + ' => ' + res.statusText)

    const data = await res.json()

    if (options?.paging || params?.has('page')) {
        return {
            total: res.headers.has('x-total-count') ? parseInt(res.headers.get('x-total-count') ?? '0') : 0,
            data,
        } as unknown as T
    }

    return data
}

export async function getRefs(): Promise<Refs> {
    const value = sessionStorage.getItem('campus/refs')

    if (value)
        return JSON.parse(value)

    const data = await fetchCampusAPI<Refs>('/refs')
    sessionStorage.setItem('campus/refs', JSON.stringify(data))
    return data
}

export async function getBranches(): Promise<Branche[]> {
    return (await getRefs()).REF_BRANCHE
}

export async function fetchAPIDomaines(filters?: {brancheId?: string, organisme?: Organisme}): Promise<Domaine[]> {
    const params = new URLSearchParams()

    if (filters?.brancheId) {
        params.append("branche", filters.brancheId)
    }

    if (filters?.organisme) {
        params.append("organisme", filters.organisme.sfid)
    }

    params.append("par-page", "10000")

    return await fetchCampusAPI(`/domaines`, { params })
}

export async function fetchAPIThematiques(filters?: {brancheId?: string, domaineId?: string}): Promise<Thematique[]> {
    const params = new URLSearchParams()

    if (filters?.brancheId) {
        params.append("branche", filters.brancheId)
    }

    if (filters?.domaineId) {
        params.append("domaine", filters.domaineId)
    }

    params.append("par-page", "10000")

    return await fetchCampusAPI(`/thematiques`, { params })
}

export async function fetchAPIModules(filters?: {
    brancheId?: string,
    domaineId?: string,
    thematiqueId?: string,
}): Promise<Module[]> {
    const params = new URLSearchParams()

    if (filters?.brancheId) {
        params.append("branche", filters.brancheId)
    }

    if (filters?.domaineId) {
        params.append("domaine", filters.domaineId)
    }

    if (filters?.thematiqueId) {
        params.append("thematique", filters.thematiqueId)
    }

    params.append("tri", "module")
    params.append("par-page", "10000")

    return await fetchCampusAPI(`/modules`, { params })
}

export function keyAPIModulesAndStages (
    authSession: OidcSession | null,
    authenticating: boolean,
    searchParams: ReadonlyURLSearchParams,
    context?: SearchContext
) {
    if (authenticating)
        return null

    const user = authSession && authSession.accessToken ? authSession.user?.email : 'anonymous'

    const params = {
        'page': searchParams.get("page") ? (parseInt(searchParams.get("page")!) - 1).toString() : "0",
        'tri': searchParams.get("tri"),
        'ordre': searchParams.get("ordre"),
        'recherche': searchParams.get("recherche") && searchParams.get("recherche")!.length >= 2
            ? searchParams.get("recherche")
            : undefined,
        'branche': context?.organisme
            ? searchParams.get("branche")
            : context?.brancheId ?? searchParams.get("branche") ?? undefined,
        'domaine': searchParams.get("domaine"),
        'thematique': searchParams.get("thematique"),
        'module': searchParams.get("module"),
        'organisme': context?.organisme?.sfid ?? searchParams.get('organisme'),
        'date-debut': searchParams.get("date-debut") && searchParams.get("date-fin")
            ? formatDate(searchParams.get("date-debut")!, "yyyy-MM-dd")
            : undefined,
        'date-fin': searchParams.get("date-debut") && searchParams.get("date-fin")
            ? formatDate(searchParams.get("date-fin")!, "yyyy-MM-dd")
            : undefined,
        'region': searchParams.get("region"),
        'ville': searchParams.get("ville"),
        'modalite': searchParams.getAll("modalite"),
        'type': searchParams.getAll("type"),
        'cofinancement': searchParams.getAll("cofinancement"),
        'dda-compatible': searchParams.getAll("dda-compatible"),
        'formation-certifiante': searchParams.getAll("formation-certifiante"),
        'libre-acces': searchParams.getAll("libre-acces"),
        'session-garantie': searchParams.getAll("session-garantie"),
    }

    return `${user}@/modules?${toSearchParams(params)}`
}

export async function fetchAPIModulesAndStages(authSession: OidcSession | null, key: string): Promise<Page<Module>> {
    const idx = key.indexOf("/modules?")+"/modules?".length
    const params = new URLSearchParams(key.substring(idx))
    params.append("avecStages", "true")

    return await fetchCampusAPI<Page<Module>>('/modules', { params, authSession })
}

export function keyAPISessions(
    authSession: OidcSession | null,
    authenticating: boolean,
    searchParams: ReadonlyURLSearchParams,
) {
    if (authenticating)
        return null

    const user = authSession && authSession.accessToken ? authSession.user?.email : 'anonymous'

    const params = {
        'page': searchParams.get("page") ? (parseInt(searchParams.get("page")!) - 1).toString() : "0",
        'tri': searchParams.get("tri"),
        'ordre': searchParams.get("ordre"),
        'date-debut': searchParams.get("date-debut") && searchParams.get("date-fin")
            ? formatDate(searchParams.get("date-debut")!, "yyyy-MM-dd")
            : undefined,
        'date-fin': searchParams.get("date-debut") && searchParams.get("date-fin")
            ? formatDate(searchParams.get("date-fin")!, "yyyy-MM-dd")
            : undefined,
        'region': searchParams.get("region"),
        'ville': searchParams.get("ville"),
        'modalite': searchParams.getAll("modalite"),
        'type': searchParams.getAll("type"),
        'cofinancement': searchParams.getAll("cofinancement"),
        'dda-compatible': searchParams.getAll("dda-compatible"),
        'formation-certifiante': searchParams.getAll("formation-certifiante"),
        'libre-acces': searchParams.getAll("libre-acces"),
        'session-garantie': searchParams.getAll("session-garantie"),
    }

    return `${user}@/sessions?${toSearchParams(params)}`
}

export async function fetchAPISessions(stage: Stage, authSession: OidcSession | null, key: string): Promise<Page<Session>> {
    const idx = key.indexOf("/sessions?")+"/sessions?".length
    const params = new URLSearchParams(key.substring(idx))

    return await fetchCampusAPI<Page<Session>>(`/stage/${stage.sfid}/sessions`, { params, authSession })
}

export async function fetchAPIOrganismes(filters?: {
    brancheId?: string,
    thematique?: Thematique
}): Promise<Organisme[]> {
    const params = new URLSearchParams()

    if (filters?.brancheId) {
        params.append("branche", filters.brancheId)
    }

    if (filters?.thematique) {
        params.append("thematique", filters.thematique.sfid)
    }

    return await fetchCampusAPI(`/organismes`, { params })
}

export async function fetchAPILieux(filters?: {
    brancheId?: string,
    stage?: Stage,
    organisme?: Organisme,
    thematique?: Thematique
}): Promise<Lieux> {
    const params = new URLSearchParams()

    if (filters?.brancheId) {
        params.append("branche", filters.brancheId)
    }

    if (filters?.stage) {
        params.append("stage", filters.stage.sfid)
    }

    if (filters?.organisme) {
        params.append("organisme", filters.organisme.sfid)
    }

    if (filters?.thematique) {
        params.append("thematique", filters.thematique.sfid)
    }

    return await fetchCampusAPI(`/lieux`, { params })
}
